import { useEffect, useId, useState, type DragEvent } from 'react';
import {
  InterchangeError,
  NUMBER_COLUMNS,
  TABLE_FORMATS,
  formatFromFileName,
  nameFromFileName,
  readTables,
  type NumberColumn,
  type ParsedTable,
  type TableFormat,
  type TableImportResult,
} from '@realmery/realm-core';
import type { TableImportTarget } from '../db/protocol.ts';

/**
 * Importing random tables (docs/PARITY-STEP-2-PLAN.md section 4), as the desktop's
 * `RandomTableImportDialog`: a file (chosen, or dropped here), pasted text, or one of the
 * desktop's example files; how to read it; a preview of every table found with what the
 * reader made of it; then a new table, or the rows added to or replacing a table's own.
 */

/** samples/interchange's table files, built into the app and fetched when chosen (vite.config.ts). */
export const TABLE_EXAMPLES = [
  ['wandering-monsters.md', 'Markdown with dice ranges'],
  ['tavern-rumours.csv', 'A spreadsheet (CSV)'],
  ['weather-from-a-pdf.txt', 'Text pasted from a PDF'],
  ['vtt-drowned-omens.json', "A virtual tabletop's roll table"],
] as const;

const MAX_SIZE = 2 * 1024 * 1024;

export function TableImport({
  existing,
  onImport,
  onCancel,
}: {
  existing: { id: string; name: string } | null;
  onImport: (tables: ParsedTable[], target: TableImportTarget) => Promise<void>;
  onCancel: () => void;
}) {
  const [source, setSource] = useState<{ text: string; fileName: string | null }>({ text: '', fileName: null });
  const [format, setFormat] = useState<TableFormat | null>(null);
  const [numbers, setNumbers] = useState<NumberColumn>('AUTO');
  const [names, setNames] = useState<string[]>([]);
  const [target, setTarget] = useState<'new' | 'append' | 'replace'>('new');
  const [problem, setProblem] = useState('');
  const [working, setWorking] = useState(false);
  const [dragging, setDragging] = useState(false);
  const ids = { format: useId(), numbers: useId(), paste: useId(), example: useId() };

  let result: TableImportResult | null = null;
  let error = '';
  if (source.text.trim() !== '') {
    try {
      result = readTables(source.text, format, numbers, source.fileName ? nameFromFileName(source.fileName) : null);
    } catch (e) {
      error = e instanceof InterchangeError ? e.message : 'This could not be read as a table.';
    }
  }
  const tableNames = result?.tables.map((t) => t.name).join('\n') ?? '';
  useEffect(() => setNames(tableNames === '' ? [] : tableNames.split('\n')), [tableNames]);

  async function takeFile(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      setProblem('That file is too large to be a table (over 2 MB).');
      return;
    }
    setProblem('');
    setSource({ text: await file.text(), fileName: file.name });
    setFormat(formatFromFileName(file.name));
  }

  async function takeExample(name: string) {
    if (!name) return;
    setProblem('');
    const response = await fetch(`/examples/${name}`).catch(() => null);
    if (!response?.ok) {
      setProblem('The example could not be fetched. Check the connection and try again.');
      return;
    }
    setSource({ text: await response.text(), fileName: name });
    setFormat(formatFromFileName(name));
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    void takeFile(event.dataTransfer.files[0]);
  }

  async function confirm() {
    if (!result) return;
    setWorking(true);
    setProblem('');
    try {
      const tables = result.tables.map((t, i) => ({ ...t, name: names[i] ?? t.name }));
      await onImport(tables, target === 'new' || !existing ? { kind: 'new' } : { kind: target, tableId: existing.id });
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'The import could not be saved.');
      setWorking(false);
    }
  }

  return (
    <section
      className={dragging ? 'panel table-import dragging' : 'panel table-import'}
      aria-label="Import a table"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <h2>Import a table</h2>
      <p className="meta">From a spreadsheet, a JSON file, a virtual tabletop's roll table, Markdown, or text pasted from a PDF. Drop a file here, or:</p>
      <div className="actions">
        <label className="button file-button">
          Choose a file…
          <input type="file" accept=".csv,.tsv,.json,.md,.markdown,.txt,text/plain,text/csv,application/json,text/markdown" hidden onChange={(e) => void takeFile(e.currentTarget.files?.[0]).then(() => (e.target.value = ''))} />
        </label>
        <label htmlFor={ids.example} className="visually-hidden">
          An example
        </label>
        <select id={ids.example} value="" onChange={(e) => void takeExample(e.currentTarget.value)}>
          <option value="">Or try an example…</option>
          {TABLE_EXAMPLES.map(([file, label]) => (
            <option key={file} value={file}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <label htmlFor={ids.paste}>Or paste a table</label>
      <textarea
        id={ids.paste}
        rows={6}
        value={source.text}
        placeholder={'1-3 | Goblins\n4-5 | Orcs\n6 | A dragon'}
        onChange={(e) => setSource({ text: e.currentTarget.value, fileName: null })}
      />
      {source.fileName && <p className="meta">From {source.fileName}</p>}

      <div className="row-fields">
        <label htmlFor={ids.format}>
          Read it as
          <select id={ids.format} value={format ?? ''} onChange={(e) => setFormat((e.currentTarget.value || null) as TableFormat | null)}>
            <option value="">Detect automatically</option>
            {(Object.keys(TABLE_FORMATS) as TableFormat[]).map((f) => (
              <option key={f} value={f}>
                {TABLE_FORMATS[f].name}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={ids.numbers}>
          The numbers beside each result
          <select id={ids.numbers} value={numbers} onChange={(e) => setNumbers(e.currentTarget.value as NumberColumn)}>
            {(Object.keys(NUMBER_COLUMNS) as NumberColumn[]).map((n) => (
              <option key={n} value={n}>
                {NUMBER_COLUMNS[n]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {result && (
        <div className="table-preview" aria-label="Preview">
          <p className="meta">
            Read as {TABLE_FORMATS[result.format].name}: {result.tables.length === 1 ? 'one table' : `${result.tables.length} tables`}.
          </p>
          {result.tables.map((table, i) => (
            <PreviewTable key={i} table={table} name={names[i] ?? table.name} onName={(name) => setNames((all) => all.map((n, j) => (j === i ? name : n)))} />
          ))}
          {existing && (
            <fieldset className="table-target">
              <legend>Where it goes</legend>
              {(['new', 'append', 'replace'] as const).map((kind) => (
                <label key={kind} className="check">
                  <input type="radio" name="table-target" checked={target === kind} onChange={() => setTarget(kind)} />
                  <span>{kind === 'new' ? 'A new table' : kind === 'append' ? `Add the rows to “${existing.name}”` : `Replace “${existing.name}”'s rows`}</span>
                </label>
              ))}
              {target !== 'new' && result.tables.length > 1 && <p className="meta">Only the first table's rows are used.</p>}
            </fieldset>
          )}
        </div>
      )}
      {problem && (
        <p className="notice-error" role="alert">
          {problem}
        </p>
      )}
      <div className="actions">
        <button type="button" className="primary" disabled={!result || working} onClick={() => void confirm()}>
          {working ? 'Importing…' : 'Import'}
        </button>
        <button type="button" onClick={onCancel} disabled={working}>
          Cancel
        </button>
      </div>
    </section>
  );
}

function PreviewTable({ table, name, onName }: { table: ParsedTable; name: string; onName: (name: string) => void }) {
  const total = table.entries.reduce((sum, e) => sum + e.weight, 0);
  const shown = table.entries.slice(0, 12);
  return (
    <div className="preview-table">
      <label>
        Name
        <input value={name} maxLength={200} onChange={(e) => onName(e.currentTarget.value)} />
      </label>
      {table.description && <p className="meta">{table.description}</p>}
      <p className="meta">
        {table.entries.length} {table.entries.length === 1 ? 'result' : 'results'} · rolled on a d{total}
      </p>
      <table className="table-rows">
        <thead>
          <tr>
            <th scope="col">Weight</th>
            <th scope="col">Result</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((entry, i) => (
            <tr key={i}>
              <td>{entry.weight}</td>
              <td>{entry.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {table.entries.length > shown.length && <p className="meta">…and {table.entries.length - shown.length} more.</p>}
      {table.notes.length > 0 && (
        <ul className="import-notes">
          {table.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
